// src/components/cctv/CameraView.js
import React, { useCallback, useEffect, useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { PrivacyScreen } from "@capacitor/privacy-screen";
import { useAtom } from "jotai";
import { appConstants } from "../../atomstore/store";
import { trackCctvStreamLoadFailed } from "../../monitoring/trackCctv";
import { isEnabledByDefault } from "../../utils/configFlag";

const FullscreenOrientation = registerPlugin("FullscreenOrientation");

const CCTV_IFRAME_ALLOW =
    "fullscreen; accelerometer; gyroscope; autoplay; encrypted-media";

const isAndroidApp = () => Capacitor.getPlatform() === "android";
const isIosApp = () => Capacitor.getPlatform() === "ios";

const postIosNative = (msg) => {
    if (!isIosApp()) return;
    try {
        window.webkit?.messageHandlers?.fpSafeArea?.postMessage(msg);
    } catch (_) {
        // Native handler not ready / non-WKWebView
    }
};

const CameraView = ({
    onBack,
    streamUrl,
    useLegacyPlayer = false,
    usePostEmbed = false,
    embedUrl,
    embedParams,
    hybridAuth = { enabled: false },
    playerMode = "legacy",
    topBanner = null,
}) => {
    const [runtimeConstants] = useAtom(appConstants);
    const restrictCctvScreenRecording = isEnabledByDefault(
        runtimeConstants?.restrictCctvScreenRecording
    );
    const isM3U8 = useLegacyPlayer && streamUrl?.includes(".m3u8");

    const formRef = useRef(null);
    const iframeRef = useRef(null);
    const failureTrackedRef = useRef(false);

    const streamEndpoint =
        streamUrl ||
        embedUrl ||
        hybridAuth?.src ||
        hybridAuth?.centerPageUrl ||
        "cctv-stream";

    const streamLoadMethod =
        usePostEmbed &&
            embedUrl &&
            embedParams?.id1 &&
            embedParams?.id2 &&
            embedParams?.hash &&
            !useLegacyPlayer &&
            !(hybridAuth?.enabled && hybridAuth?.src)
            ? "POST"
            : "GET";

    const reportStreamFailure = useCallback((message) => {
        if (failureTrackedRef.current) return;
        failureTrackedRef.current = true;
        trackCctvStreamLoadFailed({
            playerMode,
            message,
            endpoint: streamEndpoint,
            method: streamLoadMethod,
        });
    }, [playerMode, streamEndpoint, streamLoadMethod]);

    // Auto-submit hidden form whenever we have valid embed params
    useEffect(() => {
        if (useLegacyPlayer) return;
        if (
            !usePostEmbed ||
            !embedUrl ||
            !embedParams?.id1 ||
            !embedParams?.id2 ||
            !embedParams?.hash ||
            (hybridAuth?.enabled && hybridAuth?.hasError !== true)
        ) {
            return;
        }

        try {
            if (formRef.current) {
                formRef.current.submit();
            }
        } catch (e) {
            console.error("[CameraView] Failed to submit CCTV embed form", e);
            reportStreamFailure(`Embed form submit failed: ${e?.message || "unknown"}`);
        }
    }, [
        usePostEmbed,
        embedUrl,
        embedParams?.id1,
        embedParams?.id2,
        embedParams?.hash,
        hybridAuth?.enabled,
        hybridAuth?.hasError,
        useLegacyPlayer,
        reportStreamFailure,
    ]);

    // Android: FLAG_SECURE blocks screenshots and recordings.
    // iOS: plugin blurs the app switcher; native code covers the feed while
    // a screen recording is in progress (no public API to block screenshots).
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return undefined;

        if (!restrictCctvScreenRecording) {
            postIosNative("captureProtectOff");
            PrivacyScreen.disable().catch((e) => {
                console.warn("[CameraView] Failed to disable privacy screen", e);
            });
            return undefined;
        }

        let active = true;
        postIosNative("captureProtectOn");

        PrivacyScreen.enable({
            android: {
                dimBackground: true,
                preventScreenshots: true,
                privacyModeOnActivityHidden: "splash",
            },
            ios: {
                blurEffect: "dark",
            },
        })
            .then(() => {
                if (!active) {
                    postIosNative("captureProtectOff");
                    return PrivacyScreen.disable();
                }
            })
            .catch((e) => {
                console.warn("[CameraView] Failed to enable privacy screen", e);
            });

        return () => {
            active = false;
            postIosNative("captureProtectOff");
            PrivacyScreen.disable().catch((e) => {
                console.warn("[CameraView] Failed to disable privacy screen", e);
            });
        };
    }, [restrictCctvScreenRecording]);

    // After iOS element-fullscreen exit, scroll metrics can stay wrong until
    // something forces a reset — helps the back button receive taps again.
    // Native recovery is scoped to CameraView; on leave we restore app safe areas.
    useEffect(() => {
        if (!isIosApp()) return undefined;

        postIosNative("cameraEnter");
        FullscreenOrientation.lockPortrait().catch(() => {});

        const recoverScroll = () => {
            const isFs =
                document.fullscreenElement ||
                document.webkitFullscreenElement;
            if (isFs) {
                FullscreenOrientation.lockLandscape().catch(() => {});
                return;
            }
            FullscreenOrientation.lockPortrait({ rotate: true }).catch(() => {});
            postIosNative("exit");
            window.scrollTo(0, 0);
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
            window.dispatchEvent(new Event("resize"));
        };

        document.addEventListener("fullscreenchange", recoverScroll);
        document.addEventListener("webkitfullscreenchange", recoverScroll);
        return () => {
            document.removeEventListener("fullscreenchange", recoverScroll);
            document.removeEventListener("webkitfullscreenchange", recoverScroll);
            postIosNative("cameraLeave");
            FullscreenOrientation.unlock().catch(() => {});
        };
    }, []);

    // Android: WebView has no orientation.lock(); lock landscape only while
    // HTML fullscreen is active (native WebChromeClient also handles this).
    useEffect(() => {
        if (!isAndroidApp()) return undefined;

        const syncOrientation = () => {
            const fullscreenEl =
                document.fullscreenElement || document.webkitFullscreenElement;
            if (fullscreenEl) {
                FullscreenOrientation.lockLandscape().catch(() => {});
            } else {
                FullscreenOrientation.unlock().catch(() => {});
            }
        };

        document.addEventListener("fullscreenchange", syncOrientation);
        document.addEventListener("webkitfullscreenchange", syncOrientation);

        return () => {
            document.removeEventListener("fullscreenchange", syncOrientation);
            document.removeEventListener("webkitfullscreenchange", syncOrientation);
            FullscreenOrientation.unlock().catch(() => {});
        };
    }, []);

    const handleVideoError = () => {
        reportStreamFailure("Video stream playback error (m3u8)");
    };

    const handleIframeError = () => {
        reportStreamFailure("Iframe stream failed to load");
    };

    const renderPlayer = () => {
        if (isM3U8) {
            return (
                <video
                    src={streamUrl}
                    autoPlay
                    controls
                    playsInline
                    className="w-full h-full object-contain"
                    onError={handleVideoError}
                />
            );
        }

        if (hybridAuth?.enabled && hybridAuth?.src) {
            return (
                <iframe
                    title="cctv-hybrid"
                    src={hybridAuth.src}
                    className="w-full h-full"
                    allow={CCTV_IFRAME_ALLOW}
                    allowFullScreen
                    frameBorder="0"
                    onError={handleIframeError}
                />
            );
        }

        if (useLegacyPlayer) {
            return (
                <iframe
                    title="cctv"
                    src={streamUrl}
                    className="w-full h-full"
                    allow={CCTV_IFRAME_ALLOW}
                    allowFullScreen
                    frameBorder="0"
                    referrerPolicy="no-referrer-when-downgrade"
                    onError={handleIframeError}
                />
            );
        }

        if (hybridAuth?.enabled && hybridAuth?.hasError !== true && !hybridAuth?.src) {
            return <div className="text-white">Loading camera...</div>;
        }

        if (
            usePostEmbed &&
            embedUrl &&
            embedParams?.id1 &&
            embedParams?.id2 &&
            embedParams?.hash
        ) {
            return (
                <div className="w-full h-full">
                    <iframe
                        name="center-embed"
                        id="center-embed"
                        ref={iframeRef}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow={CCTV_IFRAME_ALLOW}
                        allowFullScreen
                        className="w-full h-full"
                        title="cctv-embed"
                        onError={handleIframeError}
                    />

                    <form
                        ref={formRef}
                        method="POST"
                        action={embedUrl}
                        target="center-embed"
                        style={{ display: "none" }}
                    >
                        <input type="hidden" name="id1" value={embedParams.id1} />
                        <input type="hidden" name="id2" value={embedParams.id2} />
                        <input type="hidden" name="hash" value={embedParams.hash} />
                    </form>
                </div>
            );
        }

        return (
            <iframe
                title="cctv"
                src={streamUrl}
                className="w-full h-full"
                allow={CCTV_IFRAME_ALLOW}
                allowFullScreen
                frameBorder="0"
                onError={handleIframeError}
            />
        );
    };

    const handleBack = (e) => {
        // Stop the full-screen iframe from swallowing / re-targeting the tap.
        e.preventDefault();
        e.stopPropagation();
        // Restore main-app safe areas before route change (unmount cleanup also fires).
        if (isIosApp()) {
            postIosNative("cameraLeave");
            FullscreenOrientation.unlock().catch(() => {});
        }
        if (isAndroidApp()) {
            FullscreenOrientation.unlock().catch(() => {});
        }
        if (typeof onBack === "function") onBack();
    };

    return (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
            {topBanner ? (
                <div className="relative z-[110] shrink-0 pt-[env(safe-area-inset-top)]">
                    {topBanner}
                </div>
            ) : null}

            {/* PLAYER — rendered first so the back control can stack above the iframe */}
            <div className="relative min-h-0 flex-1 overflow-hidden bg-black">
                <div className="flex h-full items-center justify-center">
                    <div className="w-full h-full">
                        {renderPlayer()}
                    </div>
                </div>

                {/*
                  Keep the back control AFTER the iframe in the DOM with a high z-index.
                  On iOS WKWebView, a full-screen cross-origin iframe often steals the first
                  tap when the button is painted underneath it — which feels like needing
                  two taps to go back.
                */}
                <button
                    type="button"
                    onClick={handleBack}
                    className="absolute top-4 left-4 z-[100] flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm pointer-events-auto"
                    aria-label="Go back"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
            </div>
        </div>
    );
};

export default CameraView;
